/**
 * HealthReportCard — the premium "Portfolio Health Report" retail SKU, redesigned
 * as a DOCUMENT (Alethos). In the rail it is a compact SUMMARY card: the
 * one-sentence verdict, the breached limits as tinted rows, and "Open full report"
 * / "Download PDF" — the report itself opens in a full-width reading view
 * (HealthReportView). Descriptive-only, SEBI-linted. Fixed block of AI credits.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Routes } from "@/lib/routes";
import {
  getPortfolioHealthReport, getPortfolioXray, getClientXray, getAiBudget, ApiError,
  type AiBudget, type PortfolioHealthReport, type XRayResponse,
  creditCost,
} from "@/lib/api";
import { GateActions } from "./CreditModals";
import { useAuth } from "@/auth";
import { useIsMobile } from "../hooks/useBreakpoint";
import { HealthReportView } from "./HealthReportView";

// F24. This was `const CREDIT_COST = 12` — a client-side copy of a price the
// SERVER owns in tools/ai_budget.CREDIT_COSTS. api.ts says plainly not to do
// this ("Never hardcode these client-side — they are re-tuned against measured
// COGS, and a stale copy quotes a price the wallet no longer charges"), and we
// have already lived the consequence: `deep_dive` moved 15 → 19 against measured
// COGS, and every hardcoded quote silently became a lie. The two agree today,
// which is exactly why it was invisible.
//
// This card ALREADY fetches the whole AiBudget and threw everything away except
// the balance, so the authoritative number was one field away the entire time.
// The literal survives only as a last-resort fallback for the render that
// happens before the budget lands.
const CREDIT_COST_FALLBACK = 12;
const MONO: React.CSSProperties = { fontFamily: "var(--a-mono)" };

function breachesOf(x: XRayResponse | null) {
  const out: { subject: string; limit: string; value: string }[] = [];
  const topH = x?.concentration?.top_holdings?.[0];
  const topSec = x?.allocation?.by_sector?.[0];
  if (topH && topH.pct > 15) out.push({ subject: topH.ticker, limit: "your limit 15%", value: `${topH.pct}%` });
  if (topSec && topSec.pct > 30) out.push({ subject: topSec.name, limit: "your limit 30%", value: `${topSec.pct}%` });
  return out;
}

/** Fictional example: opening this never runs AI or saves holdings. */
export function HealthReportPreview() {
  return (
    <details className="card" style={{ padding: 16, fontSize: 13, lineHeight: 1.6 }}>
      <summary style={{ cursor: "pointer", fontWeight: 600 }}>Preview a health report — no credits used</summary>
      <article aria-label="Example portfolio health report" style={{ marginTop: 16 }}>
        <div className="kicker">Illustrative example · fictional holdings</div>
        <h3 style={{ fontSize: 19, margin: "8px 0" }}>Three things to understand about this portfolio</h3>
        <p>This example has ₹10 lakh: ₹6 lakh in stocks and ₹4 lakh in funds. It is not your portfolio, live market data, or a recommendation.</p>
        <ol style={{ paddingLeft: 22, display: "grid", gap: 12, margin: "16px 0" }}>
          <li><strong>One stock has a large influence.</strong> Example Stock A is worth ₹1.8 lakh: 30% of the stock allocation and 18% of the whole portfolio. A move in this company can noticeably change the portfolio's value.</li>
          <li><strong>Several holdings depend on the same sector.</strong> ₹2.4 lakh is in industrial stocks: 40% of the stock allocation. More company names do not necessarily mean more diversification.</li>
          <li><strong>Fund costs deserve a closer look.</strong> ₹1 lakh is in an example Regular plan. An illustrative annual return gap of 1 percentage point versus its Direct version works out to about ₹1,000 a year at this value. This is an estimate, not a quoted fee or guaranteed saving; switching can incur tax and exit loads.</li>
        </ol>
        <h4>What the numbers cannot tell us yet</h4>
        <p>A chart of today's holdings shows how that basket performed historically. Your actual return needs dated purchases, sales and cash flows. Missing purchase prices also limit the gain calculation.</p>
        <p className="muted" style={{ marginTop: 12 }}>A report on your own holdings connects allocation, risk, performance and fund health, and explains gaps in the data. Descriptive analysis, not investment advice.</p>
      </article>
    </details>
  );
}

/**
 * `clientId` switches the whole card from the caller's own portfolio to one of
 * their clients. Until 2026-08-30 this only ever read the caller's book, so an
 * IFA could get a whole-book narrative about their personal holdings but never
 * about a client — the one version worth paying for.
 */
export function HealthReportCard({ onBudget, clientId, clientName }: {
  onBudget?: (b: AiBudget) => void;
  clientId?: string;
  clientName?: string;
}) {
  const { profile } = useAuth();
  const nav = useNavigate();
  const isMobile = useIsMobile();
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<PortfolioHealthReport | null>(null);
  const [xray, setXray] = useState<XRayResponse | null>(null);
  const [generatedOn, setGeneratedOn] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const [limitReached, setLimitReached] = useState(false);
  // The 402 itself, so the wall can tell a subscription gate from a credits gate.
  const [gateErr, setGateErr] = useState<unknown>(null);
  const [view, setView] = useState<null | "open" | "print">(null);
  // Proactive affordability. limitReached only flipped AFTER a 402, so at a zero
  // balance the button rendered fully enabled and clicking appeared to do nothing
  // — users read that as "broken", not "out of credits". Fetch the balance up
  // front and gate on it. null = not yet known → don't assert either way.
  const [remaining, setRemaining] = useState<number | null>(null);
  const [budget, setBudget] = useState<AiBudget | null>(null);
  useEffect(() => {
    let off = false;
    getAiBudget().then((b) => { if (!off) { setRemaining(b.remaining_credits); setBudget(b); onBudget?.(b); } })
      .catch(() => {});
    return () => { off = true; };
  }, [onBudget]);
  // The server's price, always, once we have it.
  const CREDIT_COST = creditCost(budget, "health_report") ?? CREDIT_COST_FALLBACK;
  // Retail only: advisers do not spend AI credits on this — they draw a monthly
  // plan allowance, so a credits balance says nothing about whether they can
  // run one. The server enforces the real limit for both.
  const isRetail = profile?.role === "individual";
  const cannotAfford = isRetail && remaining != null && remaining < CREDIT_COST;

  const run = async () => {
    if (busy) return;
    setBusy(true); setMessage(null);
    try {
      const [r, x] = await Promise.all([
        getPortfolioHealthReport(clientId),
        (clientId ? getClientXray(clientId) : getPortfolioXray()).catch(() => null),
      ]);
      if (r.budget) onBudget?.(r.budget);
      if (r.available && r.report) {
        setReport(r.report); setXray(x);
        setGeneratedOn(new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }));
      } else {
        setMessage(r.text || "Couldn't generate your health report right now.");
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 402) {
        const body = e.body as { budget?: AiBudget } | undefined;
        if (body?.budget) onBudget?.(body.budget);
        setGateErr(e);
        setLimitReached(true);
      } else {
        setMessage("Something went wrong generating your report. Please try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  const breaches = breachesOf(xray);

  return (
    <div className="alethos">
      <div style={{ background: "var(--a-surface-card)", border: "1px solid var(--a-border-subtle)", borderRadius: 20, boxShadow: "0 1px 2px rgba(12,17,16,0.04)", padding: isMobile ? "16px 14px" : "18px 20px", display: "flex", flexDirection: "column", gap: 12, fontFamily: "var(--a-body)" }}>
        <div>
          <div style={{ ...MONO, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.12em", color: "var(--a-text-muted)", display: "flex", alignItems: "center", gap: 6 }}>
            <span aria-hidden style={{ color: "var(--veris-600)" }}>✦</span> Portfolio Health Report
          </div>
          {!report && (
            <div style={{ fontSize: 12.5, color: "var(--a-text-muted)", marginTop: 6, lineHeight: 1.5 }}>
              A deep read across {clientName ? `${clientName}'s` : "your"} whole book — allocation, concentration, risk, performance and fund health, connected. Descriptive — not investment advice.
            </div>
          )}
        </div>

        {isRetail && !report && <HealthReportPreview />}

        {report ? (
          <>
            <p style={{ fontFamily: "var(--a-display)", fontSize: 16.5, fontWeight: 500, letterSpacing: "-0.015em", lineHeight: 1.38, margin: 0, color: "var(--a-text-strong)" }}>{report.headline}</p>
            {breaches.length > 0 && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {breaches.map((b) => (
                  <div key={b.subject} style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,72px)", alignItems: "center", gap: 10, padding: "10px 12px", borderRadius: 12, background: "var(--warning-bg, #FFF8EB)", border: "1px solid rgba(232,149,11,0.32)" }}>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--a-text-strong)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.subject}</div>
                      <div style={{ ...MONO, fontSize: 10.5, color: "var(--a-text-muted)" }}>{b.limit}</div>
                    </div>
                    <div style={{ fontFamily: "var(--a-display)", fontSize: 19, fontWeight: 600, textAlign: "right", color: "var(--warning-fg, #C2700A)", fontVariantNumeric: "tabular-nums" }}>{b.value}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <button className="a-btn a-btn-primary" style={{ width: "100%", padding: "10px 14px", fontSize: 13 }} onClick={() => setView("open")}>Open full report</button>
              <button className="a-btn" style={{ width: "100%", background: "none", border: "none", color: "var(--a-text-muted)", fontSize: 13, fontWeight: 600, padding: "6px", cursor: "pointer" }} onClick={() => setView("print")}>Download PDF</button>
            </div>
            {/* Both halves of this line are long tracked-out mono; on a narrow phone
                they meet, so let the section count drop to its own line. */}
            <div style={{ display: "flex", justifyContent: "space-between", flexWrap: isMobile ? "wrap" : undefined, gap: isMobile ? "2px 10px" : undefined, ...MONO, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--a-text-subtle)" }}>
              <span>Generated {generatedOn} · EOD close</span>
              <span>{report.sections.length} section{report.sections.length === 1 ? "" : "s"}</span>
            </div>
            <button className="a-btn" style={{ background: "none", border: "none", color: "var(--a-text-subtle)", fontSize: 11.5, padding: 0, alignSelf: "flex-start" }} onClick={() => { setReport(null); setXray(null); }}>↻ Regenerate ({CREDIT_COST} AI credits)</button>
          </>
        ) : (limitReached || cannotAfford) ? (
          <div style={{ background: "var(--surface-sunken, var(--ink-50))", borderRadius: 10, padding: 12, fontSize: 12.5, color: "var(--a-text-body)" }}>
            {/* Buy exactly this report (₹79 via Razorpay), subscribe, or a pack —
                whichever the server says opens the door. After payment the report
                runs by itself; the user does not press Generate twice. */}
            <GateActions err={gateErr} role={profile?.role} feature="health_report"
              fallback={`A health report costs ${CREDIT_COST} AI credits and you have ${remaining ?? 0}.`}
              onSubscribe={() => nav(Routes.Subscribe)}
              prefillEmail={profile?.email} prefillName={profile?.displayName}
              onPaid={() => {
                setLimitReached(false); setGateErr(null);
                getAiBudget().then((b) => { setRemaining(b.remaining_credits); setBudget(b); onBudget?.(b); }).catch(() => {});
                void run();
              }} />
          </div>
        ) : (
          <>
            {message && <div style={{ fontSize: 12.5, color: "var(--a-text-muted)" }}>{message}</div>}
            <button className="a-btn a-btn-primary" style={{ alignSelf: "flex-start", padding: "9px 14px", fontSize: 13 }} onClick={() => void run()} disabled={busy}>
              {busy ? "Analysing your portfolio…" : `Generate report · ${CREDIT_COST} AI credits`}
            </button>
            {busy && <div style={{ fontSize: 11.5, color: "var(--a-text-subtle)" }}>Reading your holdings, X-ray, performance and the market — a few seconds.</div>}
          </>
        )}
      </div>

      {view && report && (
        <HealthReportView report={report} xray={xray} generatedOn={generatedOn}
          preparedFor={clientName || profile?.displayName || "your portfolio"}
          autoPrint={view === "print"} onClose={() => setView(null)} />
      )}
    </div>
  );
}
